import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { lockBodyScroll, releaseAllBodyScrollLocks } from "../../helpers/scrollLock";

const SidebarContext = createContext({
  mobileNavOpen: false,
  openMobileNav: () => {},
  closeMobileNav: () => {},
  toggleMobileNav: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

export const SidebarProvider = ({ children }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { pathname } = useLocation();

  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const toggleMobileNav = useCallback(() => setMobileNavOpen((open) => !open), []);

  // Close drawer on navigation. Also drop any outstanding scroll lock: an
  // overlay unmounted by the route change never runs its cleanup, and a leaked
  // lock makes the next page unscrollable.
  useEffect(() => {
    setMobileNavOpen(false);
    releaseAllBodyScrollLocks();
  }, [pathname]);

  // Lock body scroll while the full-screen drawer is open.
  // Reference-counted — see src/helpers/scrollLock.js for why capturing and
  // restoring the previous value inline leaves the page unscrollable.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    return lockBodyScroll();
  }, [mobileNavOpen]);

  // Close on escape
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileNavOpen]);

  // Close if viewport grows past mobile
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => {
      if (e.matches) setMobileNavOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const value = useMemo(
    () => ({ mobileNavOpen, openMobileNav, closeMobileNav, toggleMobileNav }),
    [mobileNavOpen, openMobileNav, closeMobileNav, toggleMobileNav]
  );

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
};

export default SidebarContext;
